import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from '../utils/apiConfig';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user, openLoginModal } = useAuth();

  const fetchFavorites = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      setFavoriteItems([]);
      return;
    }

    const userId = user.u_id || user.user_id || user.id;
    if (!userId) {
      setFavoriteItems([]);
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/favorites/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setFavoriteItems(data);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
    
    // Listen for storage changes (login/logout)
    const handleStorage = () => fetchFavorites();
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [fetchFavorites]);

  const toggleFavorite = async (product) => {
    if (!user) {
      openLoginModal();
      return;
    }

    const userId = user.u_id || user.user_id || user.id;
    const productId = product.p_id || product.id;

    try {
      const response = await fetch(`${API_URL}/favorites/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          productId
        })
      });

      if (response.ok) {
        await fetchFavorites();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const isFavorite = (productId) => {
    // productId could be a number (id) or uuid (p_id). Check both in favorites.
    return favoriteItems.some(item => 
      (item.p_id === productId) || 
      (item.product?.p_id === productId) ||
      (item.id === productId) ||
      (item.product?.id === productId)
    );
  };

  return (
    <FavoritesContext.Provider value={{
      favoriteItems,
      toggleFavorite,
      isFavorite,
      isLoading,
      refreshFavorites: fetchFavorites
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};
